import React, { useMemo, useState } from "react";
import styles from "./estimated-bill.module.scss";
import CustomerPageLayout from "../customer-page-layout/CustomerPageLayout";
import { SolarEstimatedBillIcon, DownloadIcon } from "../../../assets/icons";
import {
	useGetPayAsYouGoSubscriptionQuery,
	useLazyDownloadPayAsYouGoBillQuery,
} from "../../../service/vending-service";
import { PayAsYouGoSubscriptionBill } from "../../../model/solar-consumption";
import { Money } from "../../../model/common/money";

const formatPrice = (amount: number, currency: string) => {
	return new Money(currency, amount).toLocaleString();
};

const formatMonthYear = (dateStr: string) => {
	const date = new Date(dateStr);
	return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

const formatBillDate = (dateStr: string) => {
	const date = new Date(dateStr);
	return date.toLocaleDateString("en-US", {
		month: "long",
		day: "2-digit",
		year: "numeric",
	});
};

const formatShortDateRange = (from: string, to: string) => {
	const fromDate = new Date(from);
	const toDate = new Date(to);
	const fmt = (d: Date) =>
		d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
	return `${fmt(fromDate)} - ${fmt(toDate)}`;
};

const formatShortDate = (dateStr: string) => {
	const date = new Date(dateStr);
	const day = date.getDate();
	const suffix =
		day === 1 || day === 21 || day === 31
			? "st"
			: day === 2 || day === 22
				? "nd"
				: day === 3 || day === 23
					? "rd"
					: "th";
	const weekday = date.toLocaleDateString("en-US", { weekday: "short" });
	return `${weekday} ${day}${suffix},`;
};

const formatTime = (dateStr: string) => {
	const date = new Date(dateStr);
	return date.toLocaleTimeString("en-US", {
		hour: "2-digit",
		minute: "2-digit",
		hour12: true,
	});
};

const SCALLOP_COUNT = 36;

const BILL_STATUS = {
	ACTIVE: 1,
	PAID: 2,
	OVERDUE: 3,
} as const;

const getBillStatusLabel = (status: number) => {
	switch (status) {
		case BILL_STATUS.PAID:
			return "Paid";
		case BILL_STATUS.OVERDUE:
			return "Overdue";
		default:
			return "Current Bill";
	}
};

const getBillStatusStyle = (status: number) => {
	switch (status) {
		case BILL_STATUS.PAID:
			return styles.paidBadge;
		case BILL_STATUS.OVERDUE:
			return styles.overdueBadge;
		default:
			return styles.currentBillBadge;
	}
};

export const EstimatedBill: React.FC = () => {
	const {
		data: subscription,
		isLoading,
		error,
		refetch,
	} = useGetPayAsYouGoSubscriptionQuery();
	const [downloadBill] = useLazyDownloadPayAsYouGoBillQuery();
	const [downloadingId, setDownloadingId] = useState<string | null>(null);
	const [expandedBillId, setExpandedBillId] = useState<string | null>(null);

	const { groupedBills, meterNumber } = useMemo(() => {
		if (!subscription?.details?.bills) {
			return { groupedBills: {}, meterNumber: "" };
		}

		const bills = [...subscription.details.bills].sort(
			(a, b) => new Date(b.from).getTime() - new Date(a.from).getTime()
		);

		const grouped: Record<string, PayAsYouGoSubscriptionBill[]> = {};
		bills.forEach((bill) => {
			const key = formatMonthYear(bill.from);
			if (!grouped[key]) grouped[key] = [];
			grouped[key].push(bill);
		});

		return {
			groupedBills: grouped,
			meterNumber: subscription.meterConfiguration?.meterNumber ?? "",
		};
	}, [subscription]);

	const handleDownload = async (billId: string) => {
		setDownloadingId(billId);
		try {
			const result = await downloadBill(billId).unwrap();
			if (result) {
				window.open(result, "_blank");
			}
		} catch {
			// silently fail
		} finally {
			setDownloadingId(null);
		}
	};

	const toggleExpand = (billId: string) => {
		setExpandedBillId((prev) => (prev === billId ? null : billId));
	};

	const renderExpandedReceipt = (bill: PayAsYouGoSubscriptionBill) => {
		const currency = bill.totalCost?.currency ?? "NGN";
		const totalCostAmount = bill.totalCost?.amount ?? 0;
		const status = bill.statusDetails?.status ?? BILL_STATUS.ACTIVE;
		const isPaid = status === BILL_STATUS.PAID;

		return (
			<div className={styles.receiptWrapper}>
				<div className={styles.currentBillCard}>
					<div className={styles.billHeader}>
						<span className={styles.billMonth}>
							{formatMonthYear(bill.from)}
						</span>
						<div className={styles.billHeaderRight}>
							{isPaid && (
								<button
									className={styles.downloadButton}
									onClick={(e) => {
										e.stopPropagation();
										handleDownload(bill.id);
									}}
									disabled={downloadingId === bill.id}
								>
									<DownloadIcon />
								</button>
							)}
							<span className={getBillStatusStyle(status)}>
								{getBillStatusLabel(status)}
							</span>
						</div>
					</div>

					<div className={styles.billDetails}>
						<div className={styles.billRow}>
							<span className={styles.billLabel}>Meter Number</span>
							<span className={styles.billValue}>{meterNumber}</span>
						</div>
						<div className={styles.billRow}>
							<span className={styles.billLabel}>Bill Date</span>
							<span className={styles.billValue}>
								{formatShortDateRange(bill.from, bill.to)}
							</span>
						</div>
						<div className={styles.billRow}>
							<span className={styles.billLabel}>Solar Energy Consumed</span>
							<span className={styles.billValue}>{bill.totalKwh} kWh</span>
						</div>
						<div className={styles.billRow}>
							<span className={styles.billLabel}>Cost per kWh</span>
							<span className={styles.billValue}>
								{formatPrice(bill.costPerKwh?.amount ?? 0, currency)}
							</span>
						</div>
					</div>

					<div className={styles.totalRow}>
						<span className={styles.totalLabel}>Total</span>
						<span className={styles.totalAmount}>
							{formatPrice(totalCostAmount, currency)}
						</span>
					</div>

					{isPaid && bill.paidDate && (
						<div className={styles.paidDateRow}>
							<span className={styles.billLabel}>Paid Date</span>
							<span className={styles.billValue}>
								{formatBillDate(bill.paidDate)}
							</span>
						</div>
					)}

					<div className={styles.scallopedEdge}>
						{Array.from({ length: SCALLOP_COUNT }).map((_, i) => (
							<div key={i} className={styles.scallopDot} />
						))}
					</div>
				</div>

				{!isPaid && (
					<button className={styles.payNowButton}>
						Pay Now
					</button>
				)}
			</div>
		);
	};

	if (isLoading) {
		return (
			<CustomerPageLayout
				title="Estimated Bill"
				icon={<SolarEstimatedBillIcon />}
			>
				<div className={styles.container}>
					<div className={styles.skeleton} />
					<div className={styles.skeletonSmall} />
					<div className={styles.skeletonSmall} />
				</div>
			</CustomerPageLayout>
		);
	}

	if (error) {
		return (
			<CustomerPageLayout
				title="Estimated Bill"
				icon={<SolarEstimatedBillIcon />}
			>
				<div className={styles.container}>
					<div className={styles.errorState}>
						<p className={styles.errorText}>
							Failed to load billing data
						</p>
						<button
							className={styles.retryButton}
							onClick={() => refetch()}
						>
							Retry
						</button>
					</div>
				</div>
			</CustomerPageLayout>
		);
	}

	return (
		<CustomerPageLayout
			title="Estimated Bill"
			icon={<SolarEstimatedBillIcon />}
		>
			<div className={styles.container}>
				<div className={styles.header}>
					<h2 className={styles.title}>Estimated Bill</h2>
					<p className={styles.subtitle}>
						Transparent, Affordable, and Pay-As-You-Use
					</p>
				</div>

				{Object.entries(groupedBills).map(([month, bills]) => (
					<div key={month} className={styles.monthGroup}>
						<span className={styles.monthLabel}>{month}</span>
						<div className={styles.billListCard}>
							{bills.map((bill) => {
								const isExpanded = expandedBillId === bill.id;
								return (
									<div key={bill.id} className={styles.billItemWrapper}>
										<div
											className={styles.billListItem}
											onClick={() => toggleExpand(bill.id)}
										>
											<span className={styles.billAmount}>
												{formatPrice(
													bill.totalCost?.amount ?? 0,
													bill.totalCost?.currency ?? "NGN"
												)}
											</span>
											<div className={styles.dateBadge}>
												<span className={styles.dateText}>
													{formatShortDate(bill.from)}
												</span>
												<span className={styles.dateText}>
													{formatTime(bill.from)}
												</span>
											</div>
										</div>
										{isExpanded && renderExpandedReceipt(bill)}
									</div>
								);
							})}
						</div>
					</div>
				))}
			</div>
		</CustomerPageLayout>
	);
};
